const STORAGE_KEY = 'admin_store_visibility';

interface VisibilityState {
  showAll: boolean;
  visibleInactiveIds: string[];
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let state: VisibilityState = loadFromStorage();
const listeners = new Set<() => void>();

function loadFromStorage(): VisibilityState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt data */ }
  return { showAll: false, visibleInactiveIds: [] };
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function notify() {
  listeners.forEach((fn) => fn());
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const getVisibilityState = (): VisibilityState => state;

/**
 * Determines if a store should be visible in admin pages.
 *
 * Rules (in order):
 * 1. isActive === true  → always visible (real toggle supersedes)
 * 2. showAll === true    → visible
 * 3. id in visibleInactiveIds → visible
 * 4. Otherwise           → hidden
 */
export const isStoreVisible = (store: { id: string; isActive: boolean }): boolean => {
  if (store.isActive) return true;
  if (state.showAll) return true;
  return state.visibleInactiveIds.includes(store.id);
};

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export const setShowAll = (showAll: boolean) => {
  state = { ...state, showAll };
  persist();
  notify();
};

export const toggleStoreVisibility = (storeId: string) => {
  const ids = state.visibleInactiveIds.includes(storeId)
    ? state.visibleInactiveIds.filter((id) => id !== storeId)
    : [...state.visibleInactiveIds, storeId];
  state = { ...state, visibleInactiveIds: ids };
  persist();
  notify();
};

export const setVisibleInactiveIds = (ids: string[]) => {
  state = { ...state, visibleInactiveIds: ids };
  persist();
  notify();
};

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export const subscribeVisibility = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
